// What a finished game looked like in material: the balance after every move
// and who took how much. Pure — the replay shows it beside the move list.
import type { GameState } from '../game/GameState.js';
import { BISHOP, EMPTY, KNIGHT, PAWN, QUEEN, ROOK, colorOf, typeOf, type Color } from '../game/types.js';

/** The classical point count. The king is priceless, so it is not counted. */
const PIECE_POINTS: Readonly<Record<number, number>> = {
  [PAWN]: 1, [KNIGHT]: 3, [BISHOP]: 3, [ROOK]: 5, [QUEEN]: 9,
};

export interface GameAnalysis {
  /** White's material minus Black's, in pawns, after each ply; index 0 is the start. */
  readonly balance: readonly number[];
  /** How many pieces each side captured. */
  readonly captures: Readonly<Record<Color, number>>;
  /** The largest material lead each side ever held. */
  readonly peakLead: Readonly<Record<Color, number>>;
  /** The biggest change a single move made to the balance. */
  readonly largestSwing: number;
}

export function materialBalance(board: Int8Array): number {
  let balance = 0;
  for (const code of board) {
    if (code === EMPTY) continue;
    const points = PIECE_POINTS[typeOf(code)] ?? 0;
    balance += colorOf(code) === 'w' ? points : -points;
  }
  return balance;
}

/** `states[i]` is the game after `i` moves, as `ReplayController` holds them. */
export function analyzeGame(states: readonly GameState[]): GameAnalysis {
  const balance = states.map((state) => materialBalance(state.position.board));
  const captures = { w: 0, b: 0 };
  for (const move of states[states.length - 1]?.moves ?? []) {
    if (move.captured !== EMPTY) captures[colorOf(move.piece)]++;
  }
  let largestSwing = 0;
  for (let i = 1; i < balance.length; i++) {
    largestSwing = Math.max(largestSwing, Math.abs((balance[i] ?? 0) - (balance[i - 1] ?? 0)));
  }
  return {
    balance,
    captures,
    peakLead: { w: Math.max(0, ...balance), b: Math.max(0, ...balance.map((b) => -b)) },
    largestSwing,
  };
}
